import { createFileRoute } from '@tanstack/react-router'
import { useMenuToggle } from '@/features/super-admin/super-admin-shell-context'
import { useState } from 'react'
import { TrendingUp, DollarSign, RefreshCw, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react'
import { SuperAdminHeader } from '@/features/super-admin/components/SuperAdminHeader'
import { usePayments, usePaymentStats, useRefundPayment, type Payment } from '@/features/super-admin/hooks/use-payments'
import { PaymentTable } from '@/features/super-admin/components/billing/PaymentTable'

export const Route = createFileRoute('/super-admin/billing/')({
  component: BillingPage,
})

function BillingPage() {
  const { onMenuToggle } = useMenuToggle()
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [dateRange, setDateRange] = useState<{ start?: string; end?: string }>({})
  const [refundingPayment, setRefundingPayment] = useState<Payment | null>(null)
  const [refundAmount, setRefundAmount] = useState<string>('')

  const { data: paymentsData, isLoading: paymentsLoading } = usePayments({
    page,
    page_size: 20,
    status: statusFilter || undefined,
    start_date: dateRange.start,
    end_date: dateRange.end,
  })

  const { data: stats, isLoading: statsLoading } = usePaymentStats()
  const refundMutation = useRefundPayment()

  const handleRefund = (payment: Payment) => {
    setRefundingPayment(payment)
    setRefundAmount('')
  }

  const confirmRefund = async () => {
    if (!refundingPayment) return

    const amount = refundAmount ? parseInt(refundAmount) * 100 : undefined
    await refundMutation.mutateAsync({ id: refundingPayment.id, amount })
    setRefundingPayment(null)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount / 100)
  }

  const statCards = [
    {
      title: 'Receita Total',
      value: stats ? formatCurrency(stats.total_revenue) : '-',
      icon: DollarSign,
      color: 'bg-green-500',
      subtext: `${stats?.successful_payments || 0} pagamentos`,
    },
    {
      title: 'Reembolsos',
      value: stats ? formatCurrency(stats.total_refunds) : '-',
      icon: RefreshCw,
      color: 'bg-purple-500',
      subtext: `${stats?.refund_count || 0} reembolsos`,
    },
    {
      title: 'Receita Líquida',
      value: stats ? formatCurrency(stats.net_revenue) : '-',
      icon: TrendingUp,
      color: 'bg-teal-500',
      subtext: 'Total - Reembolsos',
    },
    {
      title: 'Pagamentos Falhos',
      value: stats?.failed_payments?.toString() || '0',
      icon: AlertCircle,
      color: 'bg-red-500',
      subtext: 'Requerem atenção',
    },
  ]

  return (
    <div className="flex flex-col h-full">
      <SuperAdminHeader
        title="Pagamentos"
        subtitle="Gerencie pagamentos e visualize métricas financeiras"
        onMenuToggle={onMenuToggle}
      />
      <div className="flex-1 overflow-auto p-4 md:p-6 max-w-7xl mx-auto w-full">

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => (
          <div key={stat.title} className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-sm text-gray-500">{stat.title}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {statsLoading ? (
                <span className="animate-pulse bg-gray-200 rounded w-24 h-8 block" />
              ) : (
                stat.value
              )}
            </p>
            <p className="text-xs text-gray-400 mt-1">{stat.subtext}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setPage(1)
              }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Todos</option>
              <option value="succeeded">Pagos</option>
              <option value="pending">Pendentes</option>
              <option value="failed">Falhos</option>
              <option value="refunded">Reembolsados</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Data Inicial</label>
            <input
              type="date"
              value={dateRange.start || ''}
              onChange={(e) => {
                setDateRange((prev) => ({ ...prev, start: e.target.value }))
                setPage(1)
              }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Data Final</label>
            <input
              type="date"
              value={dateRange.end || ''}
              onChange={(e) => {
                setDateRange((prev) => ({ ...prev, end: e.target.value }))
                setPage(1)
              }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
            />
          </div>
          {(statusFilter || dateRange.start || dateRange.end) && (
            <button
              onClick={() => {
                setStatusFilter('')
                setDateRange({})
                setPage(1)
              }}
              className="mt-5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Limpar Filtros
            </button>
          )}
        </div>
      </div>

      {/* Payments Table */}
      <PaymentTable
        payments={paymentsData?.data || []}
        loading={paymentsLoading}
        onRefund={handleRefund}
      />

      {/* Pagination */}
      {paymentsData && paymentsData.pagination.total_pages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Mostrando página {paymentsData.pagination.page} de {paymentsData.pagination.total_pages}
            {' '}({paymentsData.pagination.total} pagamentos)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(paymentsData.pagination.total_pages, p + 1))}
              disabled={page >= paymentsData.pagination.total_pages}
              className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {refundingPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-md m-4 p-6">
            <h3 className="text-lg font-semibold mb-4">Reembolsar Pagamento</h3>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Valor original: {formatCurrency(refundingPayment.amount)}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Organização: {refundingPayment.organization_name}
              </p>
            </div>
            <div className="mb-6">
              <label className="block text-sm text-gray-700 mb-1">
                Valor do Reembolso (R$)
              </label>
              <input
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder={`Deixe vazio para reembolso total (${(refundingPayment.amount / 100).toFixed(2)})`}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500"
              />
              <p className="text-xs text-gray-400 mt-1">
                Deixe vazio para reembolsar o valor total
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRefundingPayment(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmRefund}
                disabled={refundMutation.isPending}
                className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {refundMutation.isPending ? 'Processando...' : 'Confirmar Reembolso'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}
